import { useTranslation, Trans } from 'react-i18next';
import { PauseCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { SuspendedAgency } from '@/auth/AgencyProvider';
import type { BillingRequestKind, BillingStatus } from '@/data/useBilling';
import { supportEmail } from '@/lib/config';
import { moneyCents } from '@/lib/format';

/**
 * Every office membership of this account is in an agency the operator suspended (change spec
 * operator-agency-onboarding, design 2). AppLayout renders it full screen, with no navigation: only
 * the brand bar with the language switch and the sign-out. It says which agency, since when, that the data is kept, and whom to write to
 * (`supportEmail` of the runtime config). It never shows a reason: the portal is never told one (Q4).
 *
 * agency-billing (design §9, F9): `billing` are the caller's `my_billing_status()` rows. An agency paused
 * by BILLING says a payment is past due and offers a way to pay to whoever can pay (`canPay`), the way the
 * server says (`payWith`, billing-followups 0170): the open invoice's hosted page, else the Stripe billing
 * portal or a new subscription through Checkout (`onPay(agencyId, kind)`), else the support address. Everyone else reads "ask an admin". An OPERATOR suspension,
 * or no billing row, keeps the copy above whatever the role. Payment lifts the pause through the existing
 * 60 s re-check, with no reload.
 */
export function SuspendedAgencyScreen({
  agencies,
  billing = [],
  onPay,
  payBusy = false,
  payError = null,
}: {
  agencies: SuspendedAgency[];
  billing?: BillingStatus[];
  onPay?: (agencyId: string, kind: BillingRequestKind) => void;
  payBusy?: boolean;
  payError?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const email = supportEmail();
  const date = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ''
      : new Intl.DateTimeFormat(i18n.resolvedLanguage === 'es' ? 'es-US' : 'en-US', { dateStyle: 'medium' }).format(d);
  };
  const single = agencies.length === 1 ? agencies[0] : null;
  // Only a billing row that says the pause IS billing's changes the copy.
  const byBilling = new Map(
    billing.filter((b) => b.suspended && b.suspendedSourceIsBilling).map((b) => [b.agencyId, b] as const),
  );
  const billed = agencies.map((a) => byBilling.get(a.id)).filter((b): b is BillingStatus => b !== undefined);
  const canPayPath = (b: BillingStatus) =>
    b.canPay && (b.payWith === 'invoice' ? b.payUrl !== null : b.payWith !== null && onPay !== undefined);
  // The support line stays for an operator pause, and for a payer with no pay path switched on.
  const onlyBilling = billed.length === agencies.length && agencies.length > 0;
  const payerWithoutPath = billed.some((b) => b.canPay && !canPayPath(b));
  const showContact = !onlyBilling || payerWithoutPath;
  const payContact = billed.length > 0 && payerWithoutPath;

  const payControl = (b: BillingStatus, named: boolean) => {
    const label = named ? t('agency.suspended.payNowFor', { agency: b.agencyName }) : t('agency.suspended.payNow');
    if (b.payWith === 'invoice' && b.payUrl) {
      return (
        <Button asChild>
          <a href={b.payUrl} rel="noopener noreferrer" aria-label={named ? label : undefined}>
            {t('agency.suspended.payNow')}
          </a>
        </Button>
      );
    }
    return (
      <Button type="button" disabled={payBusy} aria-label={named ? label : undefined} onClick={() => b.payWith && b.payWith !== 'invoice' && onPay?.(b.agencyId, b.payWith)}>
        {t('agency.suspended.payNow')}
      </Button>
    );
  };

  const billingActions = (b: BillingStatus, named: boolean) =>
    b.canPay ? (
      <div className="flex flex-col items-center gap-2">
        {b.amountDueCents !== null && b.amountDueCents > 0 && (
          <p className="text-base font-semibold text-ink">
            {t('agency.suspended.amountDue', { amount: moneyCents(b.amountDueCents / 100) })}
          </p>
        )}
        {canPayPath(b) && (
          <>
            {payControl(b, named)}
            <p className="text-sm text-ink-secondary">{t('agency.suspended.payHint')}</p>
          </>
        )}
      </div>
    ) : (
      <p className="text-base text-ink">{t('agency.suspended.askAdmin', { agency: b.agencyName })}</p>
    );

  const singleBilling = single ? byBilling.get(single.id) : undefined;

  return (
    <Card className="mx-auto max-w-2xl">
      <CardContent className="flex flex-col items-center gap-4 p-12 text-center">
        <span className="grid size-12 place-items-center rounded-full bg-accent-wash">
          <PauseCircle className="size-6 text-accent-base" aria-hidden="true" />
        </span>
        <h1 className="text-2xl font-semibold text-ink">
          {single ? t('agency.suspended.title', { agency: single.name }) : t('agency.suspended.titleMany')}
        </h1>
        {single ? (
          <>
            <p className="text-base text-ink-secondary">
              {singleBilling
                ? t('agency.suspended.billingBody', { date: date(single.suspendedAt) })
                : t('agency.suspended.body', { date: date(single.suspendedAt) })}
            </p>
            {singleBilling && billingActions(singleBilling, false)}
          </>
        ) : (
          <>
            <ul className="flex w-full flex-col gap-3 text-base text-ink-secondary">
              {agencies.map((a) => {
                const b = byBilling.get(a.id);
                return (
                  <li key={a.id} className="flex flex-col items-center gap-2">
                    <span>
                      {b
                        ? t('agency.suspended.billingLine', { agency: a.name, date: date(a.suspendedAt) })
                        : t('agency.suspended.line', { agency: a.name, date: date(a.suspendedAt) })}
                    </span>
                    {b && billingActions(b, true)}
                  </li>
                );
              })}
            </ul>
            <p className="text-base text-ink-secondary">{t('agency.suspended.bodyMany')}</p>
          </>
        )}
        {payError && (
          <p role="alert" className="text-base text-danger">
            {payError}
          </p>
        )}
        {showContact &&
          (email ? (
            <p className="text-base text-ink">
              <Trans
                i18nKey={payContact ? 'agency.suspended.payContact' : 'agency.suspended.contact'}
                values={{ email }}
                components={{ mail: <a href={`mailto:${email}`} className="font-medium text-accent-base underline" /> }}
              />
            </p>
          ) : (
            <p className="text-base text-ink">
              {t(payContact ? 'agency.suspended.payContactNoEmail' : 'agency.suspended.contactNoEmail')}
            </p>
          ))}
      </CardContent>
    </Card>
  );
}
